import { useState } from 'react';
import { AlertTriangle, Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';

/**
 * Header bell + Sheet inbox for vendor urgency alerts.
 * Dismissing here only clears the in-app list — system NC is left alone.
 */
export default function VendorAlertBell({ alerts, onDismiss, onClearAll }) {
  const [open, setOpen] = useState(false);
  const count = alerts?.length || 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="relative shrink-0 px-2"
        onClick={() => setOpen(true)}
        aria-label={count ? `${count} order alerts` : 'Order alerts'}
        data-testid="vendor-alert-bell"
      >
        <Bell className="w-4 h-4" />
        {count > 0 ? (
          <Badge
            variant="destructive"
            className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 text-[10px] leading-none flex items-center justify-center"
          >
            {count > 99 ? '99+' : count}
          </Badge>
        ) : null}
      </Button>

      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col pt-[calc(env(safe-area-inset-top,0px)+1.25rem)]"
      >
        <SheetHeader className="pr-8 text-left">
          <SheetTitle className="flex items-center justify-between gap-2">
            <span>Alerts</span>
            {count > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground"
                onClick={() => onClearAll?.()}
                data-testid="vendor-alerts-clear-all"
              >
                Clear all
              </Button>
            ) : null}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 flex-1 overflow-y-auto space-y-2 pb-6" data-testid="vendor-alert-inbox">
          {count === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No alerts</p>
          ) : (
            alerts.map((alert) => {
              const Icon = alert.kind === 'new' ? Bell : AlertTriangle;
              const tone =
                alert.kind === 'new'
                  ? 'border-primary/30 bg-primary/5 text-foreground'
                  : 'border-amber-300 bg-amber-50 text-amber-950';
              return (
                <div
                  key={`${alert.orderId}:${alert.kind}`}
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${tone}`}
                  data-testid={`vendor-inbox-${alert.kind}-${alert.orderId}`}
                >
                  <Icon className="w-4 h-4 mt-0.5 shrink-0 opacity-80" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-tight">{alert.title}</p>
                    {alert.body ? (
                      <p className="text-xs mt-0.5 opacity-80 leading-snug">{alert.body}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md p-1 hover:bg-black/5 text-muted-foreground"
                    aria-label="Dismiss alert"
                    onClick={() => onDismiss?.(alert)}
                    data-testid={`dismiss-inbox-${alert.orderId}-${alert.kind}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
